/**
 * Render video sources written with image syntax as playable `<video>` elements.
 *
 * Content references screen recordings as `![](/media/demo.mp4)`, which
 * markdown-it emits as an `<img>` — a broken image with no playback. The
 * conversion therefore runs on the rendered HTML rather than on tokens: by the
 * time an image reaches the renderer it may already be literal `<img>` markup
 * produced by `markdown-it-img-attrs` (`![](demo.mp4){width=600}`), so a token
 * rule alone would miss that case.
 *
 * `<img>` tags inside fenced code are escaped (`&lt;img`), so they are safe.
 */

const VIDEO_EXT_RE = /\.(?:mp4|m4v|webm|ogv|ogg|mov)(?=[?#"'&\s>]|$)/i;
const IMG_TAG_RE = /<img\b[^>]*>/gi;

/** Attributes worth carrying over from the original `<img>` markup. */
const CARRY_ATTRS = ['width', 'height', 'class', 'style', 'poster'];

function readAttr(attrs, name) {
  const match = attrs.match(
    new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i')
  );
  return match?.[1] ?? match?.[2] ?? match?.[3] ?? '';
}

function toVideo(tag) {
  // `<img ...>` → ` ...` (drop the tag name and the closing bracket)
  const attrs = tag.slice(4, -1);
  const src = readAttr(attrs, 'src');
  if (!src || !VIDEO_EXT_RE.test(src)) return tag;

  const carried = CARRY_ATTRS.map(name => {
    const value = readAttr(attrs, name);
    return value ? ` ${name}="${value}"` : '';
  }).join('');
  const alt = readAttr(attrs, 'alt');
  const label = alt ? ` aria-label="${alt}"` : '';

  return `<video src="${src}" controls playsinline preload="metadata"${carried}${label}></video>`;
}

function convert(html) {
  if (typeof html !== 'string' || !html.includes('<img')) return html;
  return html.replace(IMG_TAG_RE, toVideo);
}

export default function markdownItVideo(md) {
  const render = md.render.bind(md);
  md.render = (src, env) => convert(render(src, env));

  if (typeof md.renderInline === 'function') {
    const renderInline = md.renderInline.bind(md);
    md.renderInline = (src, env) => convert(renderInline(src, env));
  }
}
